import React, { useState, useEffect, useRef } from "react";
import {
  Grid,
  Typography,
  Select,
  MenuItem,
  Button,
  FormControl,
  InputLabel,
  TextField,
  FormHelperText,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DateRangePicker } from "react-date-range";
import enUS from "date-fns/locale/en-US";
import { addDays } from "date-fns";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";

import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import { timeOffRequest, timeoffActions } from "store/slice/timeoffSlice";

const TimeOffForm = () => {
  const dispatch = useDispatch();
  const user = useSelector((s) => s.user.user);
  const timeoffStore = useSelector((s) => s.timeoff);

  const fileInputRef = useRef(null);
   
  /* ---------- single source of truth for the picker ---------- */
  const [range, setRange] = useState({
    startDate: addDays(new Date(), 7),
    endDate: addDays(new Date(), 8),
    key: "selection",
  });

  const timeOffs = [
    { key: "dayoff", label: "Day Off" },
    { key: "vacation", label: "Vacation" },
  ];

  /* ------------------------ formik -------------------------- */
  const formik = useFormik({
    initialValues: {
      request: "",
      date: null,          // for one-day requests
      vacationDate: range, // for ranges
      note: "",
      image: null,
    },
    validationSchema: Yup.object({
      request: Yup.string().required().label('Request'),
      date: Yup.date()
        .nullable()
        .when("request", {
          is: (v) => !["vacation", "businessTrip"].includes(v),
          then: (s) => s.required("Date is required"),
        }),
      vacationDate: Yup.object({
        startDate: Yup.date().required(),
        endDate: Yup.date()
          .required()
          .min(Yup.ref("startDate"), "End must be after start"),
      }).when("request", {
        is: (v) => ["vacation", "businessTrip"].includes(v),
        then: (s) => s.required("Pick a range"),
      }),
      note: Yup.string().max(256),
      image: Yup.mixed().nullable(),
    }),
    onSubmit: async (values, { resetForm }) => {
        const { image, vacationDate, ...rest } = values;
        const formData = new FormData();
      
        formData.append("request", rest.request);
        formData.append("note", rest.note ?? "");
      
        if (rest.date) {
          formData.append("date", rest.date.toISOString());
        }
      
        if (vacationDate?.startDate && vacationDate?.endDate) {
          formData.append("vacationStartDate", vacationDate.startDate.toISOString());
          formData.append("vacationEndDate", vacationDate.endDate.toISOString());
        }
      
        if (image instanceof File && image.size > 0) {
          formData.append("image", image);
        }
        await dispatch(timeOffRequest(formData));
    }});

  useEffect(() => {
    if (timeoffStore?.success?.status) {
      formik.resetForm(); // clear all fields
      setRange({
        startDate: addDays(new Date(), 7),
        endDate: addDays(new Date(), 8),
        key: "selection",
      });
  
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }

      const timer = setTimeout(() => {
        dispatch(timeoffActions.reset());
      }, 4000);
  
      return () => clearTimeout(timer);
    }
  }, [timeoffStore?.success?.status]);

  const showRange = ["vacation", "businessTrip"].includes(
    formik.values.request
  );

  return (
    <form onSubmit={formik.handleSubmit}>
      <Grid container spacing={3}>
      {timeoffStore?.success?.status && (
            <Grid item size={{ xs: 12 }}>
                <Typography variant="body2" color="success.main">
                ✅ Your request was submitted successfully.
                </Typography>
            </Grid>
        )}

        {timeoffStore?.error?.status && (
            <Grid item size={{ xs: 12 }}>
                <Typography variant="body2" color="error">
                ❌ {timeoffStore.error.message || "Something went wrong. Please try again."}
                </Typography>
            </Grid>
        )}
        {/* ---------- category ---------- */}
        <Grid item size={{ xs: 12 }}>
          <FormControl
            fullWidth
            error={formik.touched.request && Boolean(formik.errors.request)}
          >
            <InputLabel id="request-label">Time Off Category</InputLabel>
            <Select
              labelId="request-label"
              id="request"
              name="request"
              value={formik.values.request}
              label="Time Off Category"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            >
              {timeOffs.map((item) => (
                <MenuItem key={item.key} value={item.key}>
                  {item.label}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>
              {formik.touched.request && formik.errors.request}
            </FormHelperText>
          </FormControl>
        </Grid>

        {/* ---------- date / range ---------- */}
        <Grid item size={{ xs: 12 }}>
          {showRange ? (
            /* ----- VACATION RANGE ----- */
            <DateRangePicker
              ranges={[range]}
              onChange={({ selection }) => {
                setRange(selection); // keep UI in sync
                formik.setFieldValue("vacationDate", selection);
              }}
              locale={enUS}
              minDate={new Date()}
              maxDate={addDays(new Date(), 60)}
              moveRangeOnFirstSelection={false}
              showDateDisplay={false}
            />
          ) : (
            /* ----- SINGLE DAY ----- */
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enUS}>
              <DatePicker
                label="Date"
                value={formik.values.date}
                onChange={(d) => formik.setFieldValue("date", d)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    error={formik.touched.date && Boolean(formik.errors.date)}
                    helperText={formik.touched.date && formik.errors.date}
                  />
                )}
              />
            </LocalizationProvider>
          )}
        </Grid>

        {/* ---------- attachment ---------- */}
        <Grid item size={{ xs: 12 }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Attachment (optional)
          </Typography>
          <Button
                variant="outlined"
                component="label"
                fullWidth
                sx={{
                borderStyle: "dashed",
                borderColor: "#bdbdbd",
                backgroundColor: "#fafafa",
                color: "#424242",
                textAlign: "left",
                padding: "12px 16px",
                textTransform: "none",
                '&:hover': {
                    backgroundColor: "#f0f0f0",
                }
                }}
            >
                {formik.values.image ? formik.values.image.name : "Click to upload image"}
                <input
                type="file"
                hidden
                accept="image/*"
                ref={fileInputRef}
                onChange={(event) => {
                    const file = event.currentTarget.files[0];
                    formik.setFieldValue("image", file);
                }}
                />
            </Button>
          {formik.values.image && (
            <img
              src={URL.createObjectURL(formik.values.image)}
              alt="preview"
              style={{
                marginTop: 12,
                width: "100%",
                maxHeight: 180,
                objectFit: "contain",
                borderRadius: 8,
                border: "1px solid #ccc",
              }}
            />
          )}
        </Grid>

        {/* ---------- note ---------- */}
        <Grid item size={{ xs: 12 }}>
          <TextField
            fullWidth
            multiline
            minRows={2}
            label="Note"
            name="note"
            value={formik.values.note}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.note && Boolean(formik.errors.note)}
            helperText={formik.touched.note && formik.errors.note}
          />
        </Grid>

        {/* ---------- submit ---------- */}
        <Grid item size={{ xs: 12 }}>
          <Button type="submit" variant="contained" fullWidth size="large">
            Send Request
          </Button>
        </Grid>
      </Grid>
    </form>
  );
};

export default TimeOffForm;
